import React, { useEffect, useState, useCallback } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Dispatch } from "redux";
import { IStore } from "redux/store";
import { apiActions } from "redux/_core/api";
import reportAPIS from "redux/app/report/api";

// ─── Types ───────────────────────────────────────────────────────────────────

interface VesselOption {
	value: string; // vesselId
	label: string; // vesselName
}

interface SpeedLossDoc {
	selectedDate: string;
	vesselName: string;
	vesselId: string;
	data?: {
		start_time?: { local_reading: string };
		end_time?: { local_reading: string };
		start_lat?: { local_reading: string };
		start_long?: { local_reading: string };
		end_lat?: { local_reading: string };
		end_long?: { local_reading: string };
		speed_thr_water?: { local_reading: string };
		speed_over_ground?: { local_reading: string };
		cp_orders_ordered_speed?: { local_reading?: string } | string;
		[key: string]: any;
	};
	remarks?: any[];
	category?: string;
	delayReason?: string;
	approverRemarks?: { remark: string }[];
	isApprove?: boolean;
	code?: string;
	title?: string;
	[key: string]: any;
}

interface VesselResult {
	vesselId: string;
	docs: SpeedLossDoc[];
	error?: string;
}

// ─── Main component ───────────────────────────────────────────────────────────

type OwnProps = {};
type SpeedLossReportProps = PropsFromRedux & OwnProps;

function SpeedLossReport(props: SpeedLossReportProps) {
	// Default date range: last 1 month
	const today = new Date();
	const oneMonthAgo = new Date(today);
	oneMonthAgo.setMonth(today.getMonth() - 1);
	const fmtDate = (d: Date) => d.toISOString().slice(0, 10);

	const [fromDate, setFromDate] = useState<string>(fmtDate(oneMonthAgo));
	const [toDate, setToDate] = useState<string>(fmtDate(today));

	const [allVessels, setAllVessels] = useState<VesselOption[]>([]);
	const [vesselSearch, setVesselSearch] = useState<string>("");
	const [selectedVessels, setSelectedVessels] = useState<VesselOption[]>([]);
	const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [vesselResults, setVesselResults] = useState<VesselResult[]>([]);
	const [hasGenerated, setHasGenerated] = useState<boolean>(false);

	// ── Load vessels on mount
	useEffect(() => {
		props.dispatch(
			apiActions.apiRequest({
				...reportAPIS.loadLngVesselList(), // Mocks reusing vessel list
				postExecute: {
					onSuccess({ data }) {
						const opts: VesselOption[] = (data?.vessels || data || []).map((v: any) => ({
							value: v.id ?? v._id,
							label: v.vesselName ?? v.name ?? v.id,
						}));
						setAllVessels(opts);
					},
				},
			})
		);
	}, [props.dispatch]);

	// ── Filtered vessel options for search
	const filteredVessels = allVessels.filter((v) =>
		v.label.toLowerCase().includes(vesselSearch.toLowerCase())
	);

	// ── Select / deselect vessel
	const toggleVessel = useCallback(
		(vessel: VesselOption) => {
			const already = selectedVessels.find((v) => v.value === vessel.value);
			if (already) {
				setSelectedVessels((prev) => prev.filter((v) => v.value !== vessel.value));
			} else {
				setSelectedVessels((prev) => [...prev, vessel]);
			}
		},
		[selectedVessels]
	);

	const deselectVessel = (vesselId: string) => {
		setSelectedVessels((prev) => prev.filter((v) => v.value !== vesselId));
	};

	// ── Generate report
	const generateReport = async () => {
		if (selectedVessels.length === 0) {
			setError("Please select at least one vessel.");
			return;
		}
		setIsLoading(true);
		setError(null);
		setHasGenerated(false);

		props.dispatch(
			apiActions.apiRequest({
				...reportAPIS.loadSpeedLossReport(
					selectedVessels.map((v) => v.value),
					fromDate,
					toDate
				),
				postExecute: {
					onSuccess({ data }) {
						setVesselResults(data?.data || []);
						setHasGenerated(true);
					},
					onError(err: any) {
						setError(err?.message ?? err?.error ?? "Failed to fetch Speed Loss data");
					},
					finally() {
						setIsLoading(false);
					},
				},
			})
		);
	};

	const extractRemarks = (doc: SpeedLossDoc) => {
		const categoryStr = doc.category || "";
		const delayReasonStr = doc.delayReason || "";
		if (categoryStr && delayReasonStr) return `${categoryStr} - ${delayReasonStr}`;
		return categoryStr || delayReasonStr || "";
	};

	const renderApprover = (doc: SpeedLossDoc) => {
		if (doc.isApprove === false || doc.isApprove === undefined) {
			return <span className="text-red-600 font-medium">Not Approved</span>;
		}
		let approverName = "Unknown";
		if (doc.approverRemarks && doc.approverRemarks.length > 0) {
			const remarkObj = doc.approverRemarks[doc.approverRemarks.length - 1];
			approverName = (remarkObj as any).user?.name || "System/Approver";
		} else if (doc.isApprove) {
			approverName = "System/Approver";
		}
		return <span className="text-emerald-600 font-medium">{approverName}</span>;
	};

	const renderOrderedSpeed = (doc: SpeedLossDoc) => {
		if (doc.code === "speedLossLogConfig") {
			const rawSpeed = doc.data?.cp_orders_ordered_speed;
			const cpSpeed = typeof rawSpeed === "object" ? rawSpeed?.local_reading : rawSpeed;
			return cpSpeed ?? "-";
		}
		return "-";
	};

	let renderDocs: (SpeedLossDoc & { vesselNameMatched: string })[] = [];
	vesselResults.forEach((res) => {
		const vName = allVessels.find((v) => v.value === res.vesselId)?.label || "Unknown";
		res.docs.forEach((d) => {
			renderDocs.push({ ...d, vesselNameMatched: vName });
		});
	});

	// Sort docs by Date
	renderDocs.sort((a, b) => {
		const dateA = new Date(a.selectedDate || a.createdAt);
		const dateB = new Date(b.selectedDate || b.createdAt);
		return dateA.getTime() - dateB.getTime();
	});

	return (
		<div className="bg-white m-2 rounded-lg h-[calc(100vh-98px)] overflow-y-auto">
			{/* Header */}
			<div className="flex items-center px-6 pt-6 pb-4 border-b border-gray-100">
				<span
					title="go back"
					onClick={() => window?.history?.back()}
					className="mr-3 text-lg cursor-pointer bp3-icon bp3-icon-arrow-left text-gray-600 hover:text-gray-900"
				/>
				<h2 className="text-xl font-semibold text-gray-800">SpeedLoss Report</h2>
			</div>

			{/* Filter Panel */}
			<div className="px-6 py-5 border-b border-gray-100 bg-gray-50">
				<div className="flex flex-wrap gap-4 items-end">
					{/* From Date */}
					<div>
						<label className="block text-xs font-medium text-gray-600 mb-1">
							From Date
						</label>
						<input
							type="date"
							value={fromDate}
							onChange={(e) => setFromDate(e.target.value)}
							className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
						/>
					</div>

					{/* To Date */}
					<div>
						<label className="block text-xs font-medium text-gray-600 mb-1">
							To Date
						</label>
						<input
							type="date"
							value={toDate}
							onChange={(e) => setToDate(e.target.value)}
							className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
						/>
					</div>

					{/* Vessel Selector */}
					<div className="w-64">
						<label className="block text-xs font-medium text-gray-600 mb-1">
							Select Vessels
						</label>
						<div className="relative">
							<input
								type="text"
								placeholder="Search vessel name…"
								value={vesselSearch}
								onFocus={() => setIsDropdownOpen(true)}
								onBlur={() => setTimeout(() => setIsDropdownOpen(false), 200)}
								onChange={(e) => {
									setVesselSearch(e.target.value);
									setIsDropdownOpen(true);
								}}
								className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
							/>
							{isDropdownOpen && filteredVessels.length > 0 && (
								<div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-52 overflow-y-auto">
									{filteredVessels.map((vessel) => {
										const isSelected = selectedVessels.some(
											(v) => v.value === vessel.value
										);
										return (
											<div
												key={vessel.value}
												onMouseDown={() => toggleVessel(vessel)}
												className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm hover:bg-blue-50 ${isSelected ? "bg-blue-50 text-blue-700" : "text-gray-700"
													}`}
											>
												<input
													type="checkbox"
													readOnly
													checked={isSelected}
													className="pointer-events-none"
												/>
												{vessel.label}
											</div>
										);
									})}
								</div>
							)}
							{isDropdownOpen && filteredVessels.length === 0 && (
								<div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg px-3 py-2 text-sm text-gray-400">
									No vessels found
								</div>
							)}
						</div>
					</div>

					{/* Generate Button */}
					<div>
						<label className="block text-xs font-medium text-transparent mb-1 select-none">&nbsp;</label>
						<button
							onClick={generateReport}
							disabled={isLoading}
							className="px-5 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
						>
							{isLoading ? "Loading…" : "Fetch Records"}
						</button>
					</div>
				</div>

				{/* Selected Vessel Chips */}
				{selectedVessels.length > 0 && (
					<div className="flex flex-wrap gap-2 mt-4">
						{selectedVessels.map((v) => (
							<span
								key={v.value}
								className="inline-flex items-center gap-1.5 bg-blue-100 text-blue-800 text-xs font-medium px-3 py-1 rounded-full"
							>
								{v.label}
								<button
									onClick={() => deselectVessel(v.value)}
									className="hover:text-blue-600 focus:outline-none ml-1"
									title="Remove"
								>
									<svg
										className="w-3 h-3"
										fill="currentColor"
										viewBox="0 0 20 20"
									>
										<path
											fillRule="evenodd"
											d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
											clipRule="evenodd"
										/>
									</svg>
								</button>
							</span>
						))}
					</div>
				)}

				{/* Error */}
				{error && (
					<p className="mt-3 text-sm text-red-600 font-medium">{error}</p>
				)}
			</div>

			{/* Data Grid Table */}
			<div className="px-6 py-6">
				{isLoading && (
					<div className="flex justify-center items-center py-20 text-gray-500 text-sm">
						Fetching SpeedLoss data…
					</div>
				)}

				{!isLoading && !hasGenerated && (
					<div className="flex flex-col items-center justify-center py-20 text-gray-400">
						<svg
							className="w-12 h-12 mb-3 opacity-40"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={1.5}
								d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
							/>
						</svg>
						<p className="text-sm">Select vessels and click Generate Report</p>
					</div>
				)}

				{!isLoading && hasGenerated && (
					<>
						{renderDocs.length === 0 ? (
							<div className="flex justify-center py-20 text-gray-400 text-sm">
								No SpeedLoss data found for the selected vessels and date range.
							</div>
						) : (
							<div className="overflow-x-auto border border-gray-200 sm:rounded-lg">
								<table className="min-w-full divide-y divide-gray-200">
									<thead className="bg-[#f3f4f6]">
										<tr>
											<th rowSpan={2} className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-r border-gray-300">
												Vessel
											</th>
											<th rowSpan={2} className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-r border-gray-300">
												Date
											</th>
											<th rowSpan={2} className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-r border-gray-300">
												Title
											</th>
											<th colSpan={2} className="px-3 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-r border-gray-300">
												Time
											</th>
											<th colSpan={4} className="px-3 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-r border-gray-300">
												Position
											</th>
											<th rowSpan={2} className="px-3 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-r border-gray-300">
												STW
											</th>
											<th rowSpan={2} className="px-3 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-r border-gray-300">
												SOG
											</th>
											<th rowSpan={2} className="px-3 py-2 text-center text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-r border-gray-300">
												Ordered Speed
											</th>
											<th rowSpan={2} className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-r border-gray-300">
												Remarks
											</th>
											<th rowSpan={2} className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-gray-300">
												Approver
											</th>
										</tr>
										<tr>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-r border-gray-300">
												Start
											</th>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-r border-gray-300">
												Stop
											</th>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-r border-gray-300">
												Start Latitude
											</th>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-r border-gray-300">
												Start Longitude
											</th>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-r border-gray-300">
												End Latitude
											</th>
											<th className="px-3 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b border-r border-gray-300">
												End Longitude
											</th>
										</tr>
									</thead>
									<tbody className="bg-white divide-y divide-gray-200">
										{renderDocs.map((doc, idx) => (
											<tr key={doc.id || idx} className="hover:bg-gray-50">
												<td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
													{doc.vesselNameMatched}
												</td>
												<td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
													{doc.selectedDate?.slice(0, 10) || "-"}
												</td>
												<td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200 truncate max-w-[150px]" title={doc.title || "-"}>
													{doc.title || "-"}
												</td>
												<td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
													{doc.data?.start_time?.local_reading || "-"}
												</td>
												<td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
													{doc.data?.end_time?.local_reading || "-"}
												</td>
												<td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
													{doc.data?.start_lat?.local_reading || "-"}
												</td>
												<td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
													{doc.data?.start_long?.local_reading || "-"}
												</td>
												<td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
													{doc.data?.end_lat?.local_reading || "-"}
												</td>
												<td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200">
													{doc.data?.end_long?.local_reading || "-"}
												</td>
												<td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200 text-center">
													{doc.data?.speed_thr_water?.local_reading || "-"}
												</td>
												<td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border-r border-gray-200 text-center">
													{doc.data?.speed_over_ground?.local_reading || "-"}
												</td>
												<td className="px-3 py-2 whitespace-nowrap text-center text-sm text-gray-900 border-r border-gray-200">
													{renderOrderedSpeed(doc)}
												</td>
												<td className="px-3 py-2 text-sm text-gray-900 border-r border-gray-200 truncate max-w-xs" title={extractRemarks(doc)}>
													{extractRemarks(doc)}
												</td>
												<td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 border-gray-200">
													{renderApprover(doc)}
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</>
				)}
			</div>
		</div>
	);
}

// ─── Redux ────────────────────────────────────────────────────────────────────

function mapStateToProps(store: IStore, ownProps: OwnProps) {
	return {};
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: OwnProps) {
	return { dispatch };
}

const reduxConnector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof reduxConnector>;
export default reduxConnector(SpeedLossReport);
